import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
// import styles from "./unassignModal.module.scss";
import userSlice, { RoleCode, User } from "../users/store/slice";
import Select, { StylesConfig } from "react-select";
import defaultSelectStyle from "../../utils/select";
import { UserRequestParams } from "../users/store/saga";
import { Company } from "../company-page/store/slice";
import userPageSlice from "../user-page/store/slice";

export type UnassignBookingsValues = {
  fromUser: User;
  toUser: User;
};

type CustomerOption = {
  label: string;
  value: User;
};

type Props = {
  user: User;
  onHide: () => void;
};

const UnassignModal = ({ user, onHide }: Props) => {
  const dispatch = useDispatch();

  const [customerName, setCustomerName] = useState<string>("");
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption>();

  const users = useSelector((state) => state.users.data);
  const isUsersLoading = useSelector((state) => state.users.isLoading);

  const fetchUsers = useCallback(
    (search: string, company: Company) =>
      dispatch(
        userSlice.actions.fetch({
          search,
          roleCode: RoleCode.Customer,
          onlyWithCompanies: true,
          companyId: company.id,
        } as UserRequestParams)
      ),
    [dispatch]
  );
  const resetUsers = useCallback(
    () => dispatch(userSlice.actions.reset()),
    [dispatch]
  );

  const unassignBookings = useCallback(
    (fromUser: User, toUser: User) =>
      dispatch(
        userPageSlice.actions.unassignBookings({
          fromUser,
          toUser,
        } as UnassignBookingsValues)
      ),
    [dispatch]
  );

  useEffect(() => {
    if (user.companies && user.companies.length > 0) {
      fetchUsers("", user.companies[0]);
    }

    return () => {
      resetUsers();
    };
  }, [user, fetchUsers, resetUsers]);

  const customerOptions: CustomerOption[] = useMemo(
    () =>
      users
        .filter((fetchedUser) => fetchedUser.id !== user.id)
        .map((user) => ({
          label: user.name,
          value: user,
        })),
    [users, user]
  );

  return (
    <Modal show onHide={onHide} backdrop="static" fullscreen="lg-down">
      <Modal.Header closeVariant="white" closeButton>
        <Modal.Title>Unassign bookings</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Card>
          <Card.Body>
            <Select
              value={selectedCustomer}
              inputValue={customerName}
              onInputChange={(inputValue, actionMeta) => {
                if (actionMeta.action === "input-change") {
                  setCustomerName(inputValue);

                  if (user.companies && user.companies.length > 0) {
                    fetchUsers(inputValue, user.companies[0]);
                  }
                }
              }}
              onChange={(option, actionMeta) => {
                if (option) {
                  setSelectedCustomer(option as CustomerOption);
                }

                if (actionMeta.action !== "pop-value") {
                  setCustomerName("");
                }
              }}
              options={customerOptions}
              isLoading={isUsersLoading}
              placeholder="Search for colleague"
              styles={{
                ...(defaultSelectStyle as StylesConfig<CustomerOption>),
              }}
            />
            <small className="text-secondary">
              All ({user.owned_bookings_count}) bookings will be assigned to
              another person.
            </small>
          </Card.Body>
        </Card>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            if (selectedCustomer) {
              unassignBookings(user, selectedCustomer.value);
              onHide();
            }
          }}
          disabled={!selectedCustomer}
        >
          Unassign
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UnassignModal;
